import {useState, useTransition} from 'react';
import {Plus, Pencil, Trash2, Check, AlertCircle, Play, Layers, CalendarRange} from 'lucide-react';
import {addDays, formatDistanceToNow, parseISO} from 'date-fns';
import {
  Alert,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Spinner,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {
  type QaConfig,
  type CreateQaConfigPayload,
  type FireQaConfigRange,
  type UpdateQaConfigPayload
} from '@/api/qaConfigs';
import {getApiErrorMessage} from '@/api/client';
import {useConfirm} from '@/components/shared/useConfirm';
import {
  useQaConfigs,
  useCreateQaConfig,
  useUpdateQaConfig,
  useDeleteQaConfig,
  useFireQaConfig
} from '@/api/hooks';
import {ScheduleInput} from '@/components/shared/ScheduleInput';
import {showToast} from '@/lib/toast';
import {InFilterBar} from '@/components/layout/FilterBarSlot';

type FormState = {
  policy_violation: string;
  sample_percent: number;
  schedule: string;
  enabled: boolean;
};

function blankForm(verticals: string[]): FormState {
  return {
    policy_violation: verticals[0] ?? '',
    sample_percent: 10,
    schedule: '0 9 * * 1',
    enabled: true
  };
}

function configToForm(c: QaConfig): FormState {
  return {
    policy_violation: c.policy_violation,
    sample_percent: c.sample_percent,
    schedule: c.schedule,
    enabled: c.enabled
  };
}

function formToPayload(f: FormState): CreateQaConfigPayload {
  return {
    policy_violation: f.policy_violation,
    sample_percent: f.sample_percent,
    schedule: f.schedule,
    enabled: f.enabled
  };
}

function QaConfigModal({
  initial,
  isNew,
  verticals,
  population,
  onSave,
  onClose
}: {
  readonly initial: FormState;
  readonly isNew: boolean;
  readonly verticals: string[];
  // The config's current window population, so the preview reflects real volume. Undefined for a
  // new config, which has no population yet.
  readonly population: number | undefined;
  readonly onSave: (f: FormState) => Promise<void>;
  readonly onClose: () => void;
}) {
  const [form, setForm] = useState<FormState>(initial);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((previous) => ({...previous, [k]: v}));
  }

  function submit() {
    setError(null);
    startTransition(async () => {
      try {
        await onSave(form);
        onClose();
      } catch (error_) {
        setError(getApiErrorMessage(error_, "Couldn't save the Weekly QA config."));
      }
    });
  }

  const previewText =
    population === undefined
      ? `${form.sample_percent}% of delivered submissions per run`
      : `≈${Math.min(population, Math.ceil((population * form.sample_percent) / 100))} items per run (${form.sample_percent}% of ${population} delivered last period)`;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isNew ? 'New Weekly QA configuration' : `Edit Weekly QA: ${initial.policy_violation}`}
          </DialogTitle>
          <DialogDescription>
            Per-policy-violation sampling — the percentage of delivered submissions to pull for
            review, and how often.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup className="gap-4">
          <Field>
            <FieldLabel htmlFor="qa-config-policy-violation">Policy violation</FieldLabel>
            <NativeSelect
              id="qa-config-policy-violation"
              wrapperClassName="w-full"
              value={form.policy_violation}
              disabled={!isNew}
              onChange={(e) => {
                set('policy_violation', e.target.value);
              }}>
              {verticals.map((v) => (
                <NativeSelectOption key={v} value={v}>
                  {v}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>

          <Field>
            <FieldLabel htmlFor="qa-config-sample-percent">Sample percentage</FieldLabel>
            <Input
              id="qa-config-sample-percent"
              type="number"
              min={1}
              max={100}
              step={1}
              value={form.sample_percent}
              onChange={(e) => {
                set('sample_percent', Number(e.target.value));
              }}
            />
          </Field>

          <div>
            <FieldDescription className="text-foreground font-medium">
              {previewText}
            </FieldDescription>
            <FieldDescription>
              The sample will include submissions from all analysts that contributed submissions
              that week, proportionally.
            </FieldDescription>
          </div>

          <ScheduleInput
            compact
            value={form.schedule === '' ? null : form.schedule}
            onChange={(cron) => {
              set('schedule', cron ?? '');
            }}
          />

          <Field orientation="horizontal">
            <FieldLabel htmlFor="qa-config-enabled">Active</FieldLabel>
            <Switch
              id="qa-config-enabled"
              checked={form.enabled}
              onCheckedChange={() => {
                set('enabled', !form.enabled);
              }}
            />
          </Field>

          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          ) : null}
        </FieldGroup>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              pending ||
              form.policy_violation === '' ||
              form.schedule === '' ||
              !Number.isInteger(form.sample_percent) ||
              form.sample_percent < 1 ||
              form.sample_percent > 100
            }
            onClick={submit}>
            {pending ? <Spinner /> : <Check />}
            {pending ? 'Saving…' : 'Save configuration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 A one-off run over a chosen date range. Both dates are inclusive days in the user's own time zone,
 so `to` is sent as midnight at the start of the day after the To date.
 */
function QaRangeRunModal({
  config,
  onRun,
  onClose
}: {
  readonly config: QaConfig;
  readonly onRun: (range: FireQaConfigRange) => Promise<void>;
  readonly onClose: () => void;
}) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    startTransition(async () => {
      try {
        // A bare yyyy-MM-dd is local midnight to parseISO (new Date() would read it as UTC).
        await onRun({
          from: parseISO(from).toISOString(),
          to: addDays(parseISO(to), 1).toISOString()
        });
        onClose();
      } catch (error_) {
        setError(getApiErrorMessage(error_, 'Run failed'));
      }
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Run Weekly QA for dates: {config.policy_violation}</DialogTitle>
          <DialogDescription>
            Samples {config.sample_percent}% of the submissions delivered in this range that
            haven&apos;t been QA&apos;d yet. The scheduled run is not affected.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup className="grid grid-cols-2 gap-3">
          <Field>
            <FieldLabel htmlFor="qa-range-from">From</FieldLabel>
            <Input
              id="qa-range-from"
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
              }}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="qa-range-to">To</FieldLabel>
            <Input
              id="qa-range-to"
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
              }}
            />
          </Field>
        </FieldGroup>

        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>{error}</AlertTitle>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {/* yyyy-MM-dd strings compare in date order. */}
          <Button
            type="button"
            disabled={pending || from === '' || to === '' || from > to}
            onClick={submit}>
            {pending ? <Spinner /> : <Play />}
            {pending ? 'Running…' : 'Run'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QaConfigCard({
  config,
  onEdit,
  onDelete,
  onToggle
}: {
  readonly config: QaConfig;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  readonly onToggle: () => void;
}) {
  const fireMut = useFireQaConfig();
  const [rangeOpen, setRangeOpen] = useState(false);

  async function handleFire() {
    try {
      const result = await fireMut.mutateAsync({id: config.id});
      showToast(
        result.selected === 0
          ? 'No submissions selected'
          : `Selected ${result.selected} of ${result.population_size} delivered`
      );
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Run failed'), 'error');
    }
  }

  // Errors surface in the dialog, which stays open; only success closes it and toasts.
  async function handleRangeRun(range: FireQaConfigRange) {
    const result = await fireMut.mutateAsync({id: config.id, range});
    showToast(
      result.selected === 0
        ? 'No unreviewed submissions in that range'
        : `Selected ${result.selected} of ${result.population_size} not yet QA'd`
    );
  }

  return (
    <Card size="sm">
      <CardHeader>
        {/*
          A paused config is named as such rather than dimmed: an opacity dim pulled every control on
          the card under the contrast floor, and the switch already shows the state to a sighted user.
        */}
        <CardTitle className="flex flex-wrap items-center gap-2">
          {config.policy_violation}
          {config.enabled ? null : <Badge variant="outline">Inactive</Badge>}
        </CardTitle>
        <CardDescription>
          {config.weekly_occurrences}x / week
          {config.next_run_at
            ? ` · Next run ${formatDistanceToNow(new Date(config.next_run_at), {addSuffix: true})}`
            : ''}
          {config.last_fired_at
            ? ` · Last fired ${formatDistanceToNow(new Date(config.last_fired_at), {addSuffix: true})}`
            : ''}
        </CardDescription>
        <CardAction>
          <Switch
            checked={config.enabled}
            aria-label={`${config.policy_violation} sampling enabled`}
            onCheckedChange={onToggle}
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        <FieldGroup className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field>
            <FieldLabel className="text-muted-foreground">Sample percentage</FieldLabel>
            <p className="text-xs font-medium tabular-nums">{config.sample_percent}%</p>
          </Field>
          <Field>
            <FieldLabel className="text-muted-foreground">Sample size</FieldLabel>
            <p className="text-xs font-medium tabular-nums">
              ≈{config.calculated_sample_size} items / run
            </p>
          </Field>
          <Field>
            <FieldLabel className="text-muted-foreground">Schedule</FieldLabel>
            <p className="text-muted-foreground font-mono text-xs">{config.schedule}</p>
          </Field>
        </FieldGroup>
      </CardContent>
      {/* Outline, not secondary: the secondary fill matches the card surface, so the buttons vanished into it. */}
      <CardFooter className="justify-end gap-2">
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={fireMut.isPending}
                onClick={() => void handleFire()}
              />
            }>
            {fireMut.isPending ? <Spinner /> : <Play />}
            Run now
          </TooltipTrigger>
          <TooltipContent>Fire this sample now</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={fireMut.isPending}
                onClick={() => {
                  setRangeOpen(true);
                }}
              />
            }>
            <CalendarRange />
            Run for dates…
          </TooltipTrigger>
          <TooltipContent>Sample submissions delivered in a date range you choose</TooltipContent>
        </Tooltip>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label="Edit configuration"
          onClick={onEdit}>
          <Pencil />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label="Delete configuration"
          className="hover:text-destructive-ink"
          onClick={onDelete}>
          <Trash2 />
        </Button>
      </CardFooter>
      {rangeOpen ? (
        <QaRangeRunModal
          config={config}
          onRun={handleRangeRun}
          onClose={() => {
            setRangeOpen(false);
          }}
        />
      ) : null}
    </Card>
  );
}

export function QaConfigAdmin({verticals}: {readonly verticals: string[]}) {
  const {data: configs = [], isLoading} = useQaConfigs();
  const [modal, setModal] = useState<'create' | QaConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {confirm, confirmDialog} = useConfirm();
  const [, startTransition] = useTransition();

  const createMut = useCreateQaConfig();
  const updateMut = useUpdateQaConfig();
  const deleteMut = useDeleteQaConfig();

  async function handleSave(form: FormState) {
    setError(null);
    try {
      const payload = formToPayload(form);
      if (modal === 'create') {
        await createMut.mutateAsync(payload);
      } else if (modal) {
        const updatePayload: UpdateQaConfigPayload = payload;
        await updateMut.mutateAsync({id: modal.id, payload: updatePayload});
      }

      setModal(null);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to save Weekly QA config.'));
    }
  }

  function handleToggle(config: QaConfig) {
    setError(null);
    startTransition(async () => {
      try {
        await updateMut.mutateAsync({id: config.id, payload: {enabled: !config.enabled}});
      } catch (error_) {
        setError(getApiErrorMessage(error_, 'Failed to toggle Weekly QA config.'));
      }
    });
  }

  async function handleDelete(config: QaConfig) {
    if (
      !(await confirm({title: `Delete the Weekly QA config for "${config.policy_violation}"?`}))
    ) {
      return;
    }

    setError(null);
    startTransition(async () => {
      try {
        await deleteMut.mutateAsync(config.id);
      } catch (error_) {
        setError(getApiErrorMessage(error_, 'Failed to delete Weekly QA config.'));
      }
    });
  }

  const availablePolicyViolations = verticals.filter(
    (v) => !configs.some((c) => c.policy_violation === v)
  );

  return (
    <div className="flex flex-col gap-4">
      <InFilterBar>
        <div className="flex flex-1 items-center justify-between gap-4">
          <p className="text-muted-foreground text-xs">
            <span className="text-foreground font-medium">{configs.length}</span> policy violations
            configured
          </p>

          <Button
            type="button"
            size="sm"
            disabled={availablePolicyViolations.length === 0}
            onClick={() => {
              setModal('create');
            }}>
            <Plus />
            New Weekly QA config
          </Button>
        </div>
      </InFilterBar>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>{error}</AlertTitle>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="text-muted-foreground flex h-32 items-center justify-center gap-2 text-xs">
          <Spinner />
          Loading…
        </div>
      ) : configs.length === 0 ? (
        <Empty variant="outline" className="py-10">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Layers />
            </EmptyMedia>
            <EmptyTitle>No Weekly QA Configurations</EmptyTitle>
            <EmptyDescription>
              Click the button above to add a per-policy-violation sampling schedule.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="flex flex-col gap-3">
          {configs.map((c) => (
            <QaConfigCard
              key={c.id}
              config={c}
              onEdit={() => {
                setModal(c);
              }}
              onDelete={() => {
                void handleDelete(c);
              }}
              onToggle={() => {
                handleToggle(c);
              }}
            />
          ))}
        </div>
      )}

      {modal ? (
        <QaConfigModal
          initial={modal === 'create' ? blankForm(availablePolicyViolations) : configToForm(modal)}
          isNew={modal === 'create'}
          verticals={modal === 'create' ? availablePolicyViolations : [modal.policy_violation]}
          population={modal === 'create' ? undefined : modal.population_size}
          onSave={handleSave}
          onClose={() => {
            setModal(null);
          }}
        />
      ) : null}
      {confirmDialog}
    </div>
  );
}
